import { useCallback, useEffect, useState } from 'react';
import { AlertCircle } from '../../icons';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1).
import { Button } from '../../components/shared';
import { fetchAccessLogs, type AccessLogRow } from '../../api/hr-people';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import './HrShell.css';
import './AccessLogPage.css';

/**
 * 개인정보 열람 이력 — 담당자 화면 (자리 재정의 판 3).
 *
 * ★★ **짝이 되는 화면이 직원 쪽에 먼저 있다** — 「내 인사기록카드 › 열람 기록」.
 *    거기서는 «누가 내 것을 봤나» 를 보고, 여기서는 «회사 전체에서 누가 무엇을 봤나» 를 본다.
 *    같은 줄을 두 방향에서 읽는 것이라 **같은 이벤트 목록**을 쓴다(서버가 한 곳에서 정한다).
 *
 * ★★ **세어서 요약하지 않는다.** 「이번 달 열람 N건」 같은 지표를 화면이 만들면 **로드된
 *    쪽수만큼만** 세게 되어 곧 거짓말이 된다. 서버가 세어 주기 전까지는 목록만 정직하게 준다.
 *
 * ★ **전사를 보는 사람만** — 그 밖에는 서버가 403 이고 화면은 그렇게 말하고 접힌다.
 *   「누가 무엇을 봤나」 는 팀을 이끄는 일이 아니라 회사가 점검하는 일이다(팀장에게도 안 연다).
 */

const RANGES: ReadonlyArray<{ days: number; label: string }> = [
  { days: 7, label: '최근 7일' },
  { days: 30, label: '최근 30일' },
  { days: 90, label: '최근 90일' },
  { days: 365, label: '최근 1년' },
];

/** 어떤 자격으로 열었나 — 서버가 남긴 `scope`·`via` 를 사람 말로. 옛 줄에는 없다. */
const VIA_LABEL: Record<string, string> = {
  ALL: '인사 담당', TEAM: '팀장', ADMIN: '회사 관리자',
  TEAM_LEADER: '팀장', GRANT: '담당자 부여', ROLE: '권한',
};

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function AccessLogPage() {
  const [rows, setRows] = useState<AccessLogRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [state, setState] = useState<'loading' | 'ok' | 'denied' | 'folded'>('loading');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (nextDays: number) => {
    setState('loading');
    try {
      const page = await fetchAccessLogs({ days: nextDays, limit: 50 });
      setRows(page.rows);
      setCursor(page.nextCursor);
      setState('ok');
    } catch (e) {
      // 403 = 볼 자격이 없다 · 그 밖 = 옛 서버이거나 실패. 둘은 다른 말을 해야 한다.
      const status = (e as { response?: { status?: number } })?.response?.status;
      setState(status === 403 ? 'denied' : 'folded');
    }
  }, []);

  useEffect(() => { void load(days); }, [load, days]);

  const more = async () => {
    if (!cursor || busy) return;
    setBusy(true);
    try {
      const page = await fetchAccessLogs({ days, limit: 50, cursor });
      setRows((prev) => [...prev, ...page.rows]);
      setCursor(page.nextCursor);
    } catch { /* 더 못 읽으면 그 자리에 머문다 */ } finally { setBusy(false); }
  };

  return (
    <div className="hrpage">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/access-log')}
        scope={scopeFor('/erp/hr/access-log')}
        title="개인정보 열람 이력"
        description="누가 누구의 인사정보를 열어 봤는지 남은 기록입니다"
      />

      {state === 'denied' && (
        <div className="hrpage__note">
          <AlertCircle size={13} aria-hidden /> 이 화면은 <b>전사 인사를 보는 담당자</b>만 볼 수 있습니다.
        </div>
      )}
      {state === 'folded' && (
        <div className="hrpage__note">열람 이력을 불러오지 못했습니다 — 서버가 아직 준비되지 않았을 수 있습니다.</div>
      )}

      {(state === 'ok' || state === 'loading') && (
        <>
          <div className="hrpage__note">
            직원은 자기 것을 <b>「내 인사기록카드 › 열람 기록」</b> 에서 봅니다 — 같은 줄을 반대편에서 읽는 화면입니다.
            공개 카드(이름 · 직위 · 부서)와 본인 조회는 남기지 않습니다.
          </div>

          {/* 기간 — 다른 인사 화면의 거르기 줄과 같은 모양(`hrcert__tabs`)을 그대로 쓴다. */}
          <div className="hrcert__tabs" role="group" aria-label="기간">
            {RANGES.map((r) => (
              <button
                key={r.days}
                type="button"
                className={`hrcert__tab${days === r.days ? ' is-on' : ''}`}
                aria-pressed={days === r.days}
                onClick={() => setDays(r.days)}
              >
                {r.label}
              </button>
            ))}
          </div>

          {state === 'loading' ? <p className="hrpage__empty">불러오는 중…</p>
            : rows.length === 0 ? <p className="hrpage__empty">이 기간에 남은 열람 기록이 없습니다.</p> : (
              <>
                <table className="hrpage__tbl">
                  <thead>
                    <tr><th>언제</th><th>누가</th><th>누구를</th><th>무엇을</th><th>자격</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="hrpage__dim">{when(r.ts)}</td>
                        <td>
                          {r.actor ? r.actor.name : '(지워진 계정)'}
                          {r.actor?.position && <span className="hrpage__dim">{` · ${r.actor.position}`}</span>}
                        </td>
                        <td>
                          {r.target ? r.target.name : '—'}
                          {r.target?.position && <span className="hrpage__dim">{` · ${r.target.position}`}</span>}
                        </td>
                        <td>{r.label}</td>
                        <td className="hrpage__dim">{VIA_LABEL[r.via ?? ''] ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {cursor && (
                  <Button
                    variant="primary"
                    size="compact"
                    className="hracc__more"
                    onClick={more}
                    disabled={busy}
                    loading={busy}
                  >
                    {busy ? '읽는 중…' : '더 보기'}
                  </Button>
                )}
              </>
            )}
        </>
      )}
    </div>
  );
}
